// CR036: Configurable Candidate Scoring Engine

import type {
  CandidateScoringConfig,
  CandidateSignal,
  SignalEvaluation,
  CandidateScoringResult,
  KeywordParams,
  RegexParams,
  ProximityParams,
  NegativeKeywordsParams,
} from './types';

// ============================================================================
// DEFAULT CONFIG
// ============================================================================

export const DEFAULT_CANDIDATE_SCORING_CONFIG: CandidateScoringConfig = {
  version: 'v1',
  enabled: true,
  threshold: 7,
  min_primary_ocr_score: 25,
  signals: [
    {
      id: 'sig_giftcard_term',
      name: "Contains 'gift card(s)'",
      type: 'KEYWORD',
      enabled: true,
      weight: 4,
      params: { terms: ['gift card', 'gift cards'], match_mode: 'ANY', case_sensitive: false }
    },
    {
      id: 'sig_currency_amount',
      name: 'Contains currency amount ($ + digits)',
      type: 'REGEX',
      enabled: true,
      weight: 2,
      params: { pattern: '\\$\\s*\\d{1,4}', min_matches: 1 }
    },
    {
      id: 'sig_verb_near_giftcard',
      name: "Qualifying verb near 'gift card'",
      type: 'PROXIMITY',
      enabled: true,
      weight: 4,
      params: {
        anchor_terms: ['gift card', 'gift cards'],
        near_terms: ['buy', 'purchase', 'spend', 'when you buy', 'when you purchase', 'get'],
        window_chars: 80
      }
    },
    {
      id: 'sig_bonus_reward_language',
      name: 'Bonus/reward language',
      type: 'KEYWORD',
      enabled: true,
      weight: 3,
      params: { terms: ['bonus', 'free', 'get $', 'receive $', 'earn'], match_mode: 'ANY', case_sensitive: false }
    },
    {
      id: 'sig_partner_offer_negative',
      name: 'Partner-offer language (negative)',
      type: 'NEGATIVE_KEYWORDS',
      enabled: true,
      weight: -6,
      params: {
        terms: ['partner offers', 'learn more', 'conditions apply', 'free trial', 'in value', 'streaming', 'hotels', 'travel', 'gas', 'savings'],
        mode: 'SUBTRACT'
      }
    }
  ]
};

// ============================================================================
// SIGNAL EVALUATORS
// ============================================================================

function evaluateKeywordSignal(text: string, signal: CandidateSignal): SignalEvaluation {
  const params = signal.params as KeywordParams;
  const searchText = params.case_sensitive ? text : text.toLowerCase();
  const terms = params.case_sensitive ? params.terms : params.terms.map(t => t.toLowerCase());
  
  const foundTerms: string[] = [];
  for (const term of terms) {
    if (searchText.includes(term)) {
      foundTerms.push(term);
    }
  }
  
  const fired = params.match_mode === 'ANY' 
    ? foundTerms.length > 0 
    : foundTerms.length === terms.length;
  
  return {
    signal_id: signal.id,
    signal_name: signal.name,
    fired,
    delta: fired ? signal.weight : 0,
    evidence: fired ? foundTerms.map(t => `Found: "${t}"`) : []
  };
}

function evaluateRegexSignal(text: string, signal: CandidateSignal): SignalEvaluation {
  const params = signal.params as RegexParams;
  
  try {
    const regex = new RegExp(params.pattern, 'gi');
    const matches = text.match(regex) || [];
    const fired = matches.length >= params.min_matches;
    
    return {
      signal_id: signal.id,
      signal_name: signal.name,
      fired,
      delta: fired ? signal.weight : 0,
      evidence: fired ? matches.slice(0, 5).map(m => `Match: "${m}"`) : []
    };
  } catch (e) {
    console.error(`[CR036] Invalid regex pattern for signal ${signal.id}:`, e);
    return {
      signal_id: signal.id,
      signal_name: signal.name,
      fired: false,
      delta: 0,
      evidence: [`Regex error: ${e}`]
    };
  }
}

function evaluateProximitySignal(text: string, signal: CandidateSignal): SignalEvaluation {
  const params = signal.params as ProximityParams;
  const lowerText = text.toLowerCase();
  const evidence: string[] = [];
  let fired = false;
  
  for (const anchor of params.anchor_terms) {
    const anchorLower = anchor.toLowerCase();
    let anchorIndex = lowerText.indexOf(anchorLower);
    
    while (anchorIndex !== -1) {
      const windowStart = Math.max(0, anchorIndex - params.window_chars);
      const windowEnd = Math.min(lowerText.length, anchorIndex + anchorLower.length + params.window_chars);
      const window = lowerText.substring(windowStart, windowEnd);
      
      for (const nearTerm of params.near_terms) {
        if (window.includes(nearTerm.toLowerCase())) {
          fired = true;
          evidence.push(`"${nearTerm}" near "${anchor}"`);
          break;
        }
      }
      
      if (fired) break;
      anchorIndex = lowerText.indexOf(anchorLower, anchorIndex + 1);
    }
    
    if (fired) break;
  }
  
  return {
    signal_id: signal.id,
    signal_name: signal.name,
    fired,
    delta: fired ? signal.weight : 0,
    evidence
  };
}

function evaluateNegativeKeywordsSignal(text: string, signal: CandidateSignal): SignalEvaluation {
  const params = signal.params as NegativeKeywordsParams;
  const lowerText = text.toLowerCase();
  
  const foundTerms: string[] = [];
  for (const term of params.terms) {
    if (lowerText.includes(term.toLowerCase())) {
      foundTerms.push(term);
    }
  }
  
  const fired = foundTerms.length > 0;
  
  return {
    signal_id: signal.id,
    signal_name: signal.name,
    fired,
    delta: fired ? signal.weight : 0, // Weight should already be negative
    evidence: fired ? foundTerms.map(t => `Negative: "${t}"`) : []
  };
}

function evaluateSignal(text: string, signal: CandidateSignal): SignalEvaluation {
  switch (signal.type) {
    case 'KEYWORD':
      return evaluateKeywordSignal(text, signal);
    case 'REGEX':
      return evaluateRegexSignal(text, signal);
    case 'PROXIMITY':
      return evaluateProximitySignal(text, signal);
    case 'NEGATIVE_KEYWORDS':
      return evaluateNegativeKeywordsSignal(text, signal);
    case 'CUSTOM':
      // Placeholder for future custom signal implementations
      return {
        signal_id: signal.id,
        signal_name: signal.name,
        fired: false,
        delta: 0,
        evidence: ['Custom signal not implemented']
      };
    default:
      return {
        signal_id: signal.id,
        signal_name: signal.name,
        fired: false,
        delta: 0,
        evidence: [`Unknown signal type: ${signal.type}`]
      };
  }
}

// ============================================================================
// MAIN SCORING FUNCTION
// ============================================================================

export function computeCandidateScore(
  ocrText: string,
  primaryOcrScore: number,
  config: CandidateScoringConfig
): CandidateScoringResult {
  const reasonCodes: string[] = [];
  
  // Check if scoring is enabled
  if (!config.enabled) {
    return {
      candidate_for_ai: false,
      candidate_score: 0,
      threshold: config.threshold,
      min_primary_ocr_score: config.min_primary_ocr_score,
      score_breakdown: [],
      reason_codes: ['SCORING_DISABLED']
    };
  }
  
  // Check min primary OCR score
  if (primaryOcrScore < config.min_primary_ocr_score) {
    reasonCodes.push('LOW_OCR_SCORE');
    return {
      candidate_for_ai: false,
      candidate_score: 0,
      threshold: config.threshold,
      min_primary_ocr_score: config.min_primary_ocr_score,
      score_breakdown: [],
      reason_codes: reasonCodes
    };
  }
  
  // Evaluate all enabled signals
  const scoreBreakdown: SignalEvaluation[] = [];
  let totalScore = 0;
  let hardBlockTriggered: string | undefined;
  
  for (const signal of config.signals) {
    if (!signal.enabled) continue;
    
    const evaluation = evaluateSignal(ocrText, signal);
    scoreBreakdown.push(evaluation);
    
    if (evaluation.fired) {
      totalScore += evaluation.delta;
      
      // Check for hard block
      if (signal.type === 'NEGATIVE_KEYWORDS') {
        const params = signal.params as NegativeKeywordsParams;
        if (params.mode === 'HARD_BLOCK') {
          hardBlockTriggered = signal.id;
          reasonCodes.push('HARD_BLOCK_TRIGGERED');
        }
      }
      
      // Add reason codes for fired signals
      if (evaluation.delta > 0) {
        reasonCodes.push(`SIGNAL_${signal.id.toUpperCase()}`);
      }
    }
  }
  
  // Determine if candidate
  const meetsThreshold = totalScore >= config.threshold;
  const candidateForAi = meetsThreshold && !hardBlockTriggered;
  
  if (meetsThreshold && !hardBlockTriggered) {
    reasonCodes.push('THRESHOLD_MET');
  } else if (!meetsThreshold) {
    reasonCodes.push('BELOW_THRESHOLD');
  }
  
  console.log(`[CR036] Candidate scoring: score=${totalScore}, threshold=${config.threshold}, candidate=${candidateForAi}, reasons=${reasonCodes.join(',')}`);
  
  return {
    candidate_for_ai: candidateForAi,
    candidate_score: totalScore,
    threshold: config.threshold,
    min_primary_ocr_score: config.min_primary_ocr_score,
    score_breakdown: scoreBreakdown,
    hard_block_triggered: hardBlockTriggered,
    reason_codes: reasonCodes
  };
}

// ============================================================================
// PRIMARY OCR SCORING
// ============================================================================

export function computePrimaryOcrScore(text: string): { total: number; breakdown: Record<string, number> } {
  if (!text || text.length === 0) {
    return {
      total: 0,
      breakdown: {
        alphanumeric_ratio: 0,
        currency_pattern_count: 0,
        symbol_noise_ratio: 0,
        text_length: 0,
        word_count: 0
      }
    };
  }
  
  // Calculate metrics
  const alphanumericCount = (text.match(/[a-zA-Z0-9]/g) || []).length;
  const alphanumericRatio = alphanumericCount / text.length;
  
  const currencyPatterns = text.match(/\$\s*\d+(?:\.\d{2})?/g) || [];
  const currencyPatternCount = currencyPatterns.length;
  
  const symbolCount = (text.match(/[^\w\s]/g) || []).length;
  const symbolNoiseRatio = symbolCount / text.length;
  
  const wordCount = text.split(/\s+/).filter(w => w.length > 0).length;
  
  // Compute score (0-100)
  let score = 0;
  
  // Text quality (up to 30 points)
  score += Math.min(30, alphanumericRatio * 40);
  
  // Low noise (up to 20 points)
  score += Math.max(0, 20 - symbolNoiseRatio * 100);
  
  // Text length (up to 20 points)
  const lengthScore = Math.min(20, text.length / 50);
  score += lengthScore;
  
  // Word count (up to 15 points)
  score += Math.min(15, wordCount / 3);
  
  // Currency patterns (up to 15 points)
  score += Math.min(15, currencyPatternCount * 3);
  
  return {
    total: Math.round(score),
    breakdown: {
      alphanumeric_ratio: Math.round(alphanumericRatio * 100) / 100,
      currency_pattern_count: currencyPatternCount,
      symbol_noise_ratio: Math.round(symbolNoiseRatio * 100) / 100,
      text_length: text.length,
      word_count: wordCount
    }
  };
}
